import { useCallback, useMemo, useState } from 'react';
import { useAppState } from 'src/context';
import get from 'lodash/get';
import type { TPostBookingPayload, TSubLocation, TUser, TUsersResponse } from 'src/types';
import { useAddBooking } from 'src/hooks/queries/booking/useAddBooking';
import { useQueryClient } from 'react-query';
import { DateTime } from 'luxon';
import { DATE_FORMAT, DEFAULT_TIME_ZONE } from 'src/constants';
import * as yup from 'yup';
import { formatError, prepareErrorsFromYupValidationError } from 'src/utils/helpers/errors';
import type { AxiosError } from 'axios';
import { useUpdateBooking } from 'src/hooks/queries/booking/useUpdateBooking';
import { useAuth } from 'src/utils/auth';
import { useDeleteBooking } from 'src/hooks/queries/booking/useDeleteBooking';
import { useControls } from 'src/hooks/queries/controls/useControls';
import type { TCreateEditBookingModalProps } from 'src/components/Modals/components/BookingModal/types';
import uniqBy from 'lodash/uniqBy';
import { useGetUsers } from 'src/hooks/queries/users/useGetUsers';
import { useFilterUsersByTeam } from 'src/hooks/users/useFilterUsersByTeam';
import { useFindUsers } from 'src/hooks/queries/users/useFindUsers';
import { useDebounce } from 'src/hooks/useDebounce';

type TModalStateProps = {
    handleCallOnCreateConfirmationModal: (
        newConfirmationData: TPostBookingPayload & { message: string }
    ) => void;
};

type Errors = {
    user_id?: string[];
    sublocation_id?: string[];
    booking_date?: string[];
};

export const initFormErrors: Errors = {
    user_id: [],
    sublocation_id: [],
    booking_date: [],
};

export const useModalState = (props: TModalStateProps) => {
    const [nextQuery, setNextQuery] = useState<string>('');
    const query = useDebounce(nextQuery, 1000);

    const { handleCallOnCreateConfirmationModal } = props;
    const {
        actions: { handleCloseModal },
        values: { modalData, location, range },
    } = useAppState();

    const { mutateAsync: deleteAsync, isLoading: isDeleting } = useDeleteBooking();

    // get timezone
    const { user: userData } = useAuth();
    const currentUser = get(userData, 'profile.user');
    const timeZone = get(currentUser, 'timezone', DEFAULT_TIME_ZONE);

    // get controls
    const { data: controls, isLoading: isControlsLoading } = useControls(false);
    //
    const subLocationId =
        (modalData?.options as TCreateEditBookingModalProps)?.subLocation_id || '';
    const userId = (modalData?.options as TCreateEditBookingModalProps)?.user_id || '';
    const date = ((modalData?.options as TCreateEditBookingModalProps)?.date as string) || '';
    const editedBooking = (modalData?.options as TCreateEditBookingModalProps)?.booking;

    const queryClient = useQueryClient();
    const { mutateAsync: addAsync, isLoading } = useAddBooking();
    const { mutateAsync: updateAsync, isLoading: isUpdating } = useUpdateBooking();

    const defaultBooking: TPostBookingPayload = {
        sublocation_id: subLocationId,
        user_id: userId,
        booking_date: DateTime.fromISO(date, { zone: timeZone }).toFormat(DATE_FORMAT),
        id: editedBooking?.id,
    };

    const [booking, setBooking] = useState<TPostBookingPayload>(defaultBooking);
    const [errors, setErrors] = useState<Errors>(initFormErrors);
    const [serverErrors, setServerErrors] = useState<string[]>([]);

    const schema = yup.object().shape({
        sublocation_id: yup.string().required('Location is required'),
        user_id: yup.string().required('Employee is required'),
        booking_date: yup.string().required('Date is required'),
    });

    // Getting sub locations list
    const data = queryClient.getQueryData(['sub-locations', location]);
    const subLocations = get(data, 'data', []).map((subLocation: TSubLocation) => ({
        label: subLocation.name,
        value: subLocation.id,
    }));

    // Getting users
    const {
        data: usersData,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        isFetching,
    } = useGetUsers(!currentUser.isAdmin);

    const pages = get(usersData, 'pages', []) as Array<TUsersResponse>;
    const usersNotFormatted = pages.reduce(
        (acc: TUser[], page: { data: TUser[] }) => [...acc, ...page?.data],
        []
    );

    const handleLoadMore = useCallback(async () => {
        if (isFetchingNextPage || !hasNextPage) {
            return;
        }
        await fetchNextPage();
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

    const { data: foundedUsersData } = useFindUsers(query, !currentUser.isAdmin);

    const foundedUsers = useMemo(() => {
        if (foundedUsersData && foundedUsersData.length) {
            return foundedUsersData.map((user: TUser) => ({
                label: `${user.firstName} ${user.lastName}`,
                value: user.id,
            }));
        }
        return [];
    }, [foundedUsersData]);

    const searchHandler = async (q: string) => {
        await queryClient.cancelQueries(['users'], { exact: false });
        setNextQuery(q);
    };

    const { users: calendarUsers } = useFilterUsersByTeam({
        type: currentUser?.isManager ? 'my-people' : 'all',
        skipCurrentUserManager: true,
        users:
            queryClient.getQueryData([
                'users-list-date',
                {
                    location_id: location,
                    start_date: range.from.toFormat('yyyy-MM-dd'),
                    end_date: range.to.toFormat('yyyy-MM-dd'),
                },
            ]) || [],
        skipUsersCoWorkers: true,
    });

    const users = useMemo(() => {
        return uniqBy(
            [
                ...usersNotFormatted.map((user: TUser) => ({
                    label: `${user.firstName} ${user.lastName}`,
                    value: user.id,
                })),
                ...foundedUsers,
                ...calendarUsers.map((user: TUser) => ({
                    label: `${user.firstName} ${user.lastName}`,
                    value: user.id,
                })),
            ],
            'value'
        );
    }, [calendarUsers, foundedUsers, usersNotFormatted]);

    const submitBooking = useCallback(async () => {
        const { id, ...rest } = booking;
        try {
            if (id) {
                await updateAsync(booking);
            } else {
                await addAsync(rest);
            }
            handleCloseModal();
            await queryClient.invalidateQueries('bookings');
            await queryClient.invalidateQueries([
                'users-list-date',
                {
                    location_id: location,
                    start_date: range.from.toFormat('yyyy-MM-dd'),
                    end_date: range.to.toFormat('yyyy-MM-dd'),
                },
            ]);
        } catch (err) {
            const errorCode = get(err, 'response.data.error', '');
            const errs = formatError(err as AxiosError<{ message: string }>);

            if (!id && errorCode === 'Duplication conflict') {
                handleCallOnCreateConfirmationModal({
                    ...rest,
                    overwrite: 1,
                    message: errs.length
                        ? errs[0]
                        : 'User is booked at other location, press "Save" to update this booking',
                });

                return;
            }

            setServerErrors(errs);
        }
    }, [
        booking,
        range.from,
        range.to,
        location,
        queryClient,
        addAsync,
        handleCloseModal,
        handleCallOnCreateConfirmationModal,
        updateAsync,
    ]);

    const validate = useCallback(async () => {
        try {
            await schema.validate(booking, { abortEarly: false, recursive: true });
            return true;
        } catch (err) {
            const result = prepareErrorsFromYupValidationError(
                err as unknown as yup.ValidationError
            );
            setErrors(result);
            return false;
        }
    }, [booking, schema]);

    const submit = useCallback(async () => {
        if (await validate()) {
            await submitBooking();
        }
    }, [validate, submitBooking]);

    const onChange = (value: string, name: string) => {
        setBooking({
            ...booking,
            [name]: value,
        });
        setErrors({
            ...errors,
            [name]: [],
        });
    };

    const deleteBooking = useCallback(async () => {
        try {
            await deleteAsync({
                id: booking.id as string,
                message: '',
            });
            handleCloseModal();
        } catch (err) {
            setServerErrors(formatError(err as AxiosError<{ message: string }>));
        }
    }, [booking.id, deleteAsync, handleCloseModal]);

    const handleCheckIsDateDisabled = useCallback(
        (current?: Date | undefined): boolean => {
            const today = DateTime.fromObject({}, { zone: timeZone }).startOf('day');

            const weeksVisibility = get(controls, 'booking_visibility_weeks', 53);
            if (!current) {
                return false;
            }

            const day = DateTime.fromObject({}, { zone: timeZone })
                .set({
                    day: +DateTime.fromJSDate(current).toFormat('dd'),
                    month: +DateTime.fromJSDate(current).toFormat('MM'),
                    year: +DateTime.fromJSDate(current).toFormat('yyyy'),
                })
                .startOf('day');

            return day.toMillis() > today.plus({ weeks: weeksVisibility }).endOf('week').toMillis();
        },
        [controls, timeZone]
    );

    const changeTimezone = (d: Date, tz: string) => {
        const invdate = new Date(
            d.toLocaleString('en-US', {
                timeZone: tz,
            })
        );
        const diff = d.getTime() - invdate.getTime();
        return new Date(d.getTime() - diff);
    };

    return {
        subLocations,
        isFetching: isLoading || isUpdating || isDeleting || isControlsLoading,
        users,
        booking,
        submit,
        errors,
        onChange,
        serverErrors,
        setServerErrors,
        timeZone,
        isOwnBooking: currentUser.id === booking.user_id,
        deleteBooking,
        handleCheckIsDateDisabled,
        changeTimezone,
        handleLoadMore,
        searchHandler,
        isUsersLoading: isFetchingNextPage || isFetching,
    };
};
